import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { ComputedContentSourceRevision, RevisionPageComputed } from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    Dialog,
    DialogBody,
    DialogHeader,
    Heading,
    LoadingPanel,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { ConfigureContentIntegrationSourceFlowByPage } from './integrations';
import { EditContentSourceOpenAPIDialog } from './openapi';

export interface EditContentSourceDialogProps {
    organizationId: string;
    spaceId: string;
    page: RevisionPageComputed;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}
/**
 * Dialog to edit the configuration of a content source for a page.
 */
export function EditContentSourceDialog(props: EditContentSourceDialogProps) {
    const { organizationId, spaceId, page, analyticsContext } = props;
    const { computed } = page;

    const closeDialog = useDialogClose();
    const hiveBranch = useHiveBranch();

    const onSubmit = (source: ComputedContentSourceRevision) => {
        assertIsDefined(hiveBranch);
        return toast.catch(async () => {
            await hiveBranch.change((change) => {
                change.updatePage(page.id, {
                    computed: source,
                });
            });

            switch (source.type) {
                case 'builtin:openapi':
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'edit_page_computed_source',
                        sourceType: source.type,
                        openAPIModelsPage: source.props.models,
                    });
                    break;
                case 'builtin:translation':
                    break;
                default:
                    analytics.logEvent({
                        ...analyticsContext,
                        type: 'edit_page_computed_source',
                        sourceType: source.type,
                    });
                    break;
            }

            closeDialog();
        }, 'Error while saving content source');
    };

    if (api.checkIsComputedIntegrationSourceType(computed.type)) {
        const sourceType = api.parseComputedIntegrationSourceType(computed.type);
        return (
            <Dialog>
                <DialogHeader>
                    <Heading>Configure content source</Heading>
                </DialogHeader>
                <React.Suspense
                    fallback={
                        <DialogBody>
                            <LoadingPanel />
                        </DialogBody>
                    }
                >
                    <DialogBody>
                        <ConfigureContentIntegrationSourceFlowByPage
                            organizationId={organizationId}
                            spaceId={spaceId}
                            page={page}
                            onSubmit={onSubmit}
                            integrationName={sourceType.integration}
                            contentSourceId={sourceType.contentSourceId}
                        />
                    </DialogBody>
                </React.Suspense>
            </Dialog>
        );
    }

    switch (computed.type) {
        case 'builtin:openapi': {
            return (
                <EditContentSourceOpenAPIDialog
                    organizationId={organizationId}
                    computedContentSource={computed}
                    onSubmit={onSubmit}
                />
            );
        }
        case 'builtin:translation': {
            throw new Error('Translation content source should not be edited');
        }
        default:
            assertNever(computed.type);
    }
}
